import { MomentSignatures, PreconfiguredMomentFunction } from './types'

import moment, { Moment } from 'moment'

import locale from './locale'

let configured = false

const clearConfig = (): void => {
  configured = false
}

// On each navigation the locale might have changed so we need to reconfigure moment
const handleRoutechange = (): void => {
  document.addEventListener('turbolinks:before-visit', clearConfig, {
    once: true,
  })
}

const configureMoment = (): void => {
  if (!configured) {
    moment.locale(locale.value)
    handleRoutechange()
    configured = true
  }
}

function createPreconfiguredMomentFunction<T extends MomentSignatures>(
  fn: T,
  receiver: typeof moment
): PreconfiguredMomentFunction {
  return function (...args) {
    configureMoment()

    return fn.apply(receiver, args)
  }
}

// Automatically configures moment with the user's locale on invocation / property access / method call
const momentProxy: typeof moment = new Proxy(moment, {
  apply(
    target: typeof moment,
    thisArg: typeof moment,
    args: Parameters<typeof moment>
  ): Moment {
    configureMoment()

    return target.apply(thisArg, args)
  },
  get<T extends keyof typeof moment>(
    target: typeof moment,
    prop: T,
    receiver: typeof moment
  ): PreconfiguredMomentFunction | typeof moment[T] {
    if (typeof target[prop] === 'function') {
      return createPreconfiguredMomentFunction(
        target[prop] as MomentSignatures,
        receiver
      )
    }

    return Reflect.get(target, prop, receiver)
  },
})

const teardown = (): void => {
  configured = false
}

export { momentProxy as SfTime, teardown }
